import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const CONSENT_TYPES = [
  { key: 'NOTIFICATION_SMS', label: 'SMS Notifications', channel: 'sms' },
  { key: 'NOTIFICATION_EMAIL', label: 'Email Notifications', channel: 'email' },
  { key: 'NOTIFICATION_VOICE', label: 'Voice Notifications', channel: 'voice' },
  { key: 'CAMPAIGN_SMS', label: 'SMS Campaigns', channel: 'sms' },
  { key: 'CAMPAIGN_EMAIL', label: 'Email Campaigns', channel: 'email' },
  { key: 'CAMPAIGN_VOICE', label: 'Voice Campaigns', channel: 'voice' },
  { key: 'FEEDBACK_SMS', label: 'SMS Feedback', channel: 'sms' },
  { key: 'FEEDBACK_EMAIL', label: 'Email Feedback', channel: 'email' },
  { key: 'VOICE_AI', label: 'Voice AI', channel: 'voice' },
];

const STATUS_CONFIG = {
  OPT_IN: { label: 'Opt-In', bg: '#dcfce7', color: '#166534' },
  OPT_OUT: { label: 'Opt-Out', bg: '#fee2e2', color: '#991b1b' },
  UNKNOWN: { label: 'Unknown', bg: '#f3f4f6', color: '#6b7280' },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.UNKNOWN;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: config.bg, color: config.color, whiteSpace: 'nowrap',
    }}>
      {config.label}
    </span>
  );
}

function ConsentManagement({ practiceId }) {
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [patientDetail, setPatientDetail] = useState(null);
  const [updating, setUpdating] = useState(null);
  const limit = 25;

  const loadStats = useCallback(async () => {
    try {
      const res = await api.getConsentStats(practiceId);
      if (res.success) setStats(res.data);
    } catch (err) {
      console.error('Error loading consent stats:', err);
    }
  }, [practiceId]);

  const loadPatients = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit, offset: page * limit };
      if (search) params.search = search;
      if (filterType) params.consentType = filterType;
      if (filterStatus) params.status = filterStatus;
      const res = await api.getConsentPatients(practiceId, params);
      if (res.success) {
        setPatients(res.data.patients || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error('Error loading consent patients:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, search, filterType, filterStatus, page]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadPatients(); }, [loadPatients]);

  const loadPatientDetail = async (patientId) => {
    if (expandedPatient === patientId) {
      setExpandedPatient(null);
      setPatientDetail(null);
      return;
    }
    try {
      const res = await api.getPatientConsents(practiceId, patientId);
      if (res.success) {
        setPatientDetail(res.data);
        setExpandedPatient(patientId);
      }
    } catch (err) {
      console.error('Error loading patient consents:', err);
    }
  };

  const handleToggleConsent = async (patientId, consentType, currentStatus) => {
    const newStatus = currentStatus === 'OPT_IN' ? 'OPT_OUT' : 'OPT_IN';
    setUpdating(`${patientId}-${consentType}`);
    try {
      await api.updatePatientConsent(practiceId, patientId, consentType, {
        status: newStatus,
        source: 'MANUAL',
        sourceDetail: 'Updated via Consent Management UI',
      });
      // Reload detail and list
      const res = await api.getPatientConsents(practiceId, patientId);
      if (res.success) setPatientDetail(res.data);
      loadPatients();
      loadStats();
    } catch (err) {
      alert('Error updating consent: ' + err.message);
    } finally {
      setUpdating(null);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Consent Management</h2>
          <p style={s.subtitle}>View and manage patient communication consent across all channels.</p>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div style={s.statsRow}>
          {Object.entries(stats).map(([type, counts]) => {
            const typeInfo = CONSENT_TYPES.find(t => t.key === type);
            if (!typeInfo) return null;
            return (
              <div key={type} style={s.statCard}>
                <div style={s.statLabel}>{typeInfo.label}</div>
                <div style={s.statValues}>
                  <span style={{ color: '#166534', fontWeight: 600 }}>{counts.OPT_IN || 0}</span>
                  <span style={{ color: '#6b7280' }}>/</span>
                  <span style={{ color: '#991b1b', fontWeight: 600 }}>{counts.OPT_OUT || 0}</span>
                  <span style={{ color: '#6b7280' }}>/</span>
                  <span style={{ color: '#6b7280' }}>{counts.UNKNOWN || 0}</span>
                </div>
                <div style={s.statLegend}>In / Out / Unknown</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div style={s.filterRow}>
        <input
          type="text"
          placeholder="Search patients..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          style={s.searchInput}
        />
        <select
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
          style={s.select}
        >
          <option value="">All Types</option>
          {CONSENT_TYPES.map(t => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
          style={s.select}
        >
          <option value="">All Statuses</option>
          <option value="OPT_IN">Opt-In</option>
          <option value="OPT_OUT">Opt-Out</option>
          <option value="UNKNOWN">Unknown</option>
        </select>
      </div>

      {/* Patient list */}
      <div style={s.card}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>
        ) : patients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>No patients found.</div>
        ) : (
          <>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Patient</th>
                  <th style={s.th}>Phone</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>SMS</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Voice</th>
                  <th style={s.th}>Source</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((p) => (
                  <React.Fragment key={p.patient_id || p.id}>
                    <tr
                      style={{ ...s.tr, cursor: 'pointer', background: expandedPatient === (p.patient_id || p.id) ? '#f0f9ff' : undefined }}
                      onClick={() => loadPatientDetail(p.patient_id || p.id)}
                    >
                      <td style={s.td}>
                        <div style={{ fontWeight: 600, color: '#111827' }}>{p.first_name} {p.last_name}</div>
                      </td>
                      <td style={s.td}>{p.phone || '—'}</td>
                      <td style={s.td}>{p.email || '—'}</td>
                      <td style={s.td}><StatusBadge status={p.notification_sms || p.NOTIFICATION_SMS || 'OPT_IN'} /></td>
                      <td style={s.td}><StatusBadge status={p.notification_email || p.NOTIFICATION_EMAIL || 'OPT_IN'} /></td>
                      <td style={s.td}><StatusBadge status={p.notification_voice || p.NOTIFICATION_VOICE || 'OPT_IN'} /></td>
                      <td style={s.td}>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{p.source || '—'}</span>
                      </td>
                    </tr>
                    {/* Expanded detail row */}
                    {expandedPatient === (p.patient_id || p.id) && patientDetail && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <div style={s.detailPanel}>
                            <div style={s.detailTitle}>All Consent Types</div>
                            <div style={s.detailGrid}>
                              {CONSENT_TYPES.map((ct) => {
                                const consent = (patientDetail.consents || []).find(c => c.consent_type === ct.key);
                                // Default missing records to OPT_IN — matches PatientConsent.canContact
                                // (line 108-110): missing = allowed to contact. List column also
                                // defaults to OPT_IN (see the row cells above) so both stay consistent.
                                const status = consent?.status || 'OPT_IN';
                                const source = consent?.source || '—';
                                const updated = consent?.updated_at;
                                const isUpdating = updating === `${p.patient_id || p.id}-${ct.key}`;
                                return (
                                  <div key={ct.key} style={s.detailItem}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{ct.label}</span>
                                      <StatusBadge status={status} />
                                    </div>
                                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                      Source: {source} {updated ? ` | ${new Date(updated).toLocaleDateString()}` : ''}
                                    </div>
                                    <button
                                      style={{
                                        ...s.toggleBtn,
                                        background: status === 'OPT_IN' ? '#fee2e2' : '#dcfce7',
                                        color: status === 'OPT_IN' ? '#991b1b' : '#166534',
                                        opacity: isUpdating ? 0.5 : 1,
                                      }}
                                      disabled={isUpdating}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleConsent(p.patient_id || p.id, ct.key, status);
                                      }}
                                    >
                                      {isUpdating ? '...' : status === 'OPT_IN' ? 'Opt Out' : 'Opt In'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={s.pagination}>
                <button
                  style={s.pageBtn}
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  Page {page + 1} of {totalPages} ({total} patients)
                </span>
                <button
                  style={s.pageBtn}
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  header: { marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 },
  subtitle: { fontSize: 14, color: '#6b7280', margin: '4px 0 0' },

  statsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 12, marginBottom: 20,
  },
  statCard: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: '14px 16px', textAlign: 'center',
  },
  statLabel: { fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  statValues: { fontSize: 18, marginTop: 6, display: 'flex', gap: 4, justifyContent: 'center' },
  statLegend: { fontSize: 10, color: '#9ca3af', marginTop: 4 },

  filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  searchInput: {
    flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, outline: 'none',
  },
  select: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
    fontSize: 13, background: '#fff', outline: 'none', minWidth: 140,
  },

  card: {
    background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
    overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5,
    borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', fontSize: 13, color: '#374151' },

  detailPanel: {
    padding: '16px 20px', background: '#f0f9ff', borderTop: '1px solid #e0f2fe',
  },
  detailTitle: { fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 12 },
  detailGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 10,
  },
  detailItem: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
    padding: '10px 12px',
  },
  toggleBtn: {
    marginTop: 8, padding: '4px 12px', borderRadius: 6, border: 'none',
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },

  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    gap: 16, padding: '14px 0',
  },
  pageBtn: {
    padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db',
    background: '#fff', fontSize: 13, cursor: 'pointer',
  },
};

export default ConsentManagement;
